const SUPPORTED_POSITIONS = new Set(['top', 'bottom']);
const SUPPORTED_TYPES = new Set(['point', 'range']);
const MAX_IMPORT_ISSUES = 3;

const formatIssueList = (issues) => {
  const visibleIssues = issues.slice(0, MAX_IMPORT_ISSUES);
  const suffix = issues.length > MAX_IMPORT_ISSUES
    ? ` ${issues.length - MAX_IMPORT_ISSUES} more issue${issues.length - MAX_IMPORT_ISSUES === 1 ? '' : 's'} found.`
    : '';
  return `${visibleIssues.join(' ')}${suffix}`;
};

const getValidTimestamp = (value) => {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }

  if (typeof value === 'number' || typeof value === 'string') {
    if (typeof value === 'string' && value.trim() === '') return null;
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : null;
  }

  return null;
};

const trimOptionalString = (value) => {
  if (value === undefined || value === null) return '';
  return typeof value === 'string' ? value.trim() : null;
};

const sanitizeTags = (tags, issues, path) => {
  if (tags === undefined) return [];

  if (!Array.isArray(tags)) {
    issues.push(`${path} must be an array of text tags.`);
    return [];
  }

  const cleanedTags = [];
  const seenTags = new Set();
  tags.forEach((tag, index) => {
    const cleanedTag = trimOptionalString(tag);
    if (!cleanedTag) {
      issues.push(`${path}[${index}] must be a non-empty text tag.`);
      return;
    }
    if (!seenTags.has(cleanedTag)) {
      cleanedTags.push(cleanedTag);
      seenTags.add(cleanedTag);
    }
  });

  return cleanedTags;
};

const getImageUrl = (event, issues, path) => {
  const imageValue = event.imageUrl ?? event.imageURL ?? event.image;
  const imageUrl = trimOptionalString(imageValue);

  if (imageUrl === null) {
    issues.push(`${path}.imageUrl must be text when provided.`);
    return '';
  }

  return imageUrl;
};

const sanitizeImportedEvent = (event, index, usedIds, issues) => {
  const path = `Event ${index + 1}`;

  if (!event || typeof event !== 'object' || Array.isArray(event)) {
    issues.push(`${path} must be an object.`);
    return null;
  }

  const id = event.id;
  if (typeof id !== 'number' || !Number.isFinite(id)) {
    issues.push(`${path} must have a numeric id.`);
  } else if (usedIds.has(id)) {
    issues.push(`${path} has a duplicate id.`);
  } else {
    usedIds.add(id);
  }

  const title = trimOptionalString(event.title);
  if (!title) {
    issues.push(`${path} must have a non-empty title.`);
  }

  const description = trimOptionalString(event.description);
  if (description === null) {
    issues.push(`${path}.description must be text when provided.`);
  }

  const startTime = getValidTimestamp(event.date);
  if (!Number.isFinite(startTime)) {
    issues.push(`${path} must have a valid date.`);
  }

  const hasEndDate = event.endDate !== undefined && event.endDate !== null && event.endDate !== '';
  const endTime = hasEndDate ? getValidTimestamp(event.endDate) : null;
  if (hasEndDate && !Number.isFinite(endTime)) {
    issues.push(`${path}.endDate must be a valid date when provided.`);
  }

  const explicitType = event.type;
  if (explicitType !== undefined && !SUPPORTED_TYPES.has(explicitType)) {
    issues.push(`${path}.type must be "point" or "range".`);
  }

  const type = explicitType || (hasEndDate ? 'range' : 'point');
  if (type === 'range' && !hasEndDate) {
    issues.push(`${path} is a range event but does not have an end date.`);
  }
  if (type === 'point' && hasEndDate) {
    issues.push(`${path} is a point event but also has an end date.`);
  }
  if (Number.isFinite(startTime) && Number.isFinite(endTime) && endTime < startTime) {
    issues.push(`${path}.endDate must be on or after its date.`);
  }

  const position = event.position || (type === 'range' ? 'bottom' : 'top');
  if (!SUPPORTED_POSITIONS.has(position)) {
    issues.push(`${path}.position must be "top" or "bottom".`);
  }

  const tags = sanitizeTags(event.tags, issues, `${path}.tags`);
  const imageUrl = getImageUrl(event, issues, path);

  if (issues.length > 0) return null;

  return {
    id,
    title,
    description: description || '',
    date: startTime,
    endDate: type === 'range' ? endTime : null,
    type,
    position: type === 'range' ? 'bottom' : position,
    tags,
    ...(imageUrl ? { imageUrl } : {})
  };
};

export const getTimelineFromImport = (parsedBackup) => {
  const timeline = parsedBackup?.timeline || parsedBackup;
  if (!timeline || typeof timeline !== 'object' || Array.isArray(timeline)) {
    throw new Error('Backup file does not contain a timeline.');
  }

  const name = trimOptionalString(timeline.name);
  if (!name) {
    throw new Error('Imported timeline must have a name.');
  }

  if (timeline.events !== undefined && !Array.isArray(timeline.events)) {
    throw new Error('Imported timeline events must be an array.');
  }

  if (timeline.tags !== undefined && !Array.isArray(timeline.tags)) {
    throw new Error('Imported timeline tags must be an array.');
  }

  const issues = [];
  const topLevelTags = sanitizeTags(timeline.tags, issues, 'Imported timeline tags');
  const usedIds = new Set();
  const events = (timeline.events || []).map((event, index) => (
    sanitizeImportedEvent(event, index, usedIds, issues)
  ));

  if (issues.length > 0) {
    throw new Error(`Imported timeline has invalid data. ${formatIssueList(issues)}`);
  }

  const eventTags = events.flatMap(event => event.tags);
  const tags = Array.from(new Set([...topLevelTags, ...eventTags]));

  return {
    ...timeline,
    name,
    events,
    tags
  };
};
